import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Globe, Palette, Upload, Trash2, Users, HelpCircle, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiError, fileToDataUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useTour } from "@/components/tour/TourProvider";
import { FOTOBOOTH_TEMPLATES } from "@/lib/constants";

export default function SettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [ton, setTon] = useState(null);
  const [branding, setBranding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyBranding, setBusyBranding] = useState(false);
  const { lang, setLang } = useLang();
  const { startTour } = useTour();

  useEffect(() => {
    api.get("/settings").then((r) => {
      setTenant(r.data);
      setTon(r.data.tonality || {});
    });
    api.get("/settings/branding").then((r) => setBranding(r.data));
  }, []);

  const saveTonality = async () => {
    setBusy(true);
    try {
      await api.patch("/settings/tonality", { tonality: ton });
      toast.success("Tonalitätsprofil gespeichert – wird bei allen KI-Texten verwendet");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveBranding = async () => {
    setBusyBranding(true);
    try {
      await api.patch("/settings/branding", { branding });
      toast.success("Branding gespeichert – wird in Fotobooth & Content Studio verwendet");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyBranding(false);
    }
  };

  const uploadLogo = async (file) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      if (dataUrl.length > 1_800_000) {
        toast.error("Logo zu groß (max. ~1,3 MB). Bitte kleinere Datei wählen.");
        return;
      }
      setBranding((b) => ({ ...b, logo_url: dataUrl }));
      toast.success("Logo geladen – bitte speichern");
    } catch {
      toast.error("Logo konnte nicht gelesen werden");
    }
  };

  if (!tenant || !ton || !branding)
    return <div className="space-y-3 max-w-2xl"><Skeleton className="h-10 w-56" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-5 max-w-2xl pb-10">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold">Einstellungen</h1>
        <p className="text-sm text-muted-foreground">{tenant.name}</p>
      </div>

      {/* Branding */}
      <Card data-testid="settings-branding-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display flex items-center gap-2"><Palette className="h-4 w-4 text-primary" /> Händler-Branding</CardTitle>
          <p className="text-xs text-muted-foreground">Logo, Farben und Standard-Hintergrund für Fotobooth & Content Studio.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-28 rounded-lg border border-border bg-secondary/40 flex items-center justify-center overflow-hidden">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="settings-logo-preview" />
              ) : (
                <span className="text-[10px] text-muted-foreground">Kein Logo</span>
              )}
            </div>
            <div className="flex gap-2">
              <label className="inline-flex">
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="settings-logo-upload" />
                <Button variant="outline" size="sm" className="gap-1.5 pointer-events-none"><Upload className="h-3.5 w-3.5" /> Logo hochladen</Button>
              </label>
              {branding.logo_url && (
                <Button variant="ghost" size="sm" className="gap-1.5 text-red-300" onClick={() => setBranding((b) => ({ ...b, logo_url: null }))} data-testid="settings-logo-remove">
                  <Trash2 className="h-3.5 w-3.5" /> Entfernen
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Firmenname</Label>
              <Input value={branding.firmenname || ""} onChange={(e) => setBranding((b) => ({ ...b, firmenname: e.target.value }))} data-testid="settings-firmenname-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={branding.website || ""} onChange={(e) => setBranding((b) => ({ ...b, website: e.target.value }))} data-testid="settings-website-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Telefon</Label>
              <Input value={branding.telefon || ""} onChange={(e) => setBranding((b) => ({ ...b, telefon: e.target.value }))} data-testid="settings-telefon-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Adresse</Label>
              <Input value={branding.adresse || ""} onChange={(e) => setBranding((b) => ({ ...b, adresse: e.target.value }))} data-testid="settings-adresse-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Hauptfarbe</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.hauptfarbe || "#38bdf8"} onChange={(e) => setBranding((b) => ({ ...b, hauptfarbe: e.target.value }))} className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer" data-testid="settings-hauptfarbe-input" />
                <span className="text-xs text-muted-foreground font-mono">{branding.hauptfarbe}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Akzentfarbe (optional)</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.akzentfarbe || "#0ea5e9"} onChange={(e) => setBranding((b) => ({ ...b, akzentfarbe: e.target.value }))} className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer" data-testid="settings-akzentfarbe-input" />
                <span className="text-xs text-muted-foreground font-mono">{branding.akzentfarbe || "–"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Standard-Fotobooth-Hintergrund</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FOTOBOOTH_TEMPLATES.map((tpl) => (
                <button key={tpl.key} onClick={() => setBranding((b) => ({ ...b, fotobooth_hintergrund: tpl.key }))}
                  className={`rounded-xl border p-2 text-left transition-colors ${branding.fotobooth_hintergrund === tpl.key ? "border-primary ring-1 ring-primary/50" : "border-border hover:border-primary/40"}`}
                  data-testid={`settings-bg-${tpl.key}`}>
                  <div className="h-8 w-full rounded-md" style={{ background: tpl.swatch }} />
                  <div className="text-[11px] font-medium mt-1">{tpl.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
            <div>
              <div className="text-sm">Logo als Wasserzeichen auf Fotobooth-Bildern</div>
              <div className="text-xs text-muted-foreground">Position und Deckkraft einstellbar</div>
            </div>
            <Switch checked={!!branding.wasserzeichen_aktiv} onCheckedChange={(v) => setBranding((b) => ({ ...b, wasserzeichen_aktiv: v }))} data-testid="settings-watermark-switch" />
          </div>
          {branding.wasserzeichen_aktiv && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Logo-Position</Label>
                <Select value={branding.logo_position || "unten_rechts"} onValueChange={(v) => setBranding((b) => ({ ...b, logo_position: v }))}>
                  <SelectTrigger data-testid="settings-logo-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unten_rechts">Unten rechts</SelectItem>
                    <SelectItem value="unten_links">Unten links</SelectItem>
                    <SelectItem value="oben_rechts">Oben rechts</SelectItem>
                    <SelectItem value="oben_links">Oben links</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Deckkraft: {Math.round((branding.wasserzeichen_deckkraft ?? 0.85) * 100)}%</Label>
                <Slider value={[Math.round((branding.wasserzeichen_deckkraft ?? 0.85) * 100)]} min={20} max={100} step={5}
                  onValueChange={([v]) => setBranding((b) => ({ ...b, wasserzeichen_deckkraft: v / 100 }))} data-testid="settings-watermark-opacity-slider" />
              </div>
            </div>
          )}

          <Button onClick={saveBranding} disabled={busyBranding} className="gap-2" data-testid="settings-branding-save-button">
            {busyBranding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Branding speichern
          </Button>
        </CardContent>
      </Card>

      {/* Sprache */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base font-display flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /> Sprache / Language</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-[220px]" data-testid="settings-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">UI-Sprache · Fahrzeugtexte werden auf Deutsch generiert</span>
        </CardContent>
      </Card>

      {/* Tonalitaet */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">Händler-Tonalität</CardTitle>
          <p className="text-xs text-muted-foreground">Dieses Profil steuert alle KI-generierten Texte (Börsentitel, Beschreibungen, Social Media, WhatsApp).</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Markenname</Label>
              <Input value={ton.haendler || ""} onChange={(e) => setTon((t) => ({ ...t, haendler: e.target.value }))} data-testid="settings-haendler-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Schreibstil</Label>
              <Select value={ton.stil || "seriös, premium"} onValueChange={(v) => setTon((t) => ({ ...t, stil: v }))}>
                <SelectTrigger data-testid="settings-stil-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["seriös, premium", "seriös", "emotional", "premium", "sportlich", "sachlich"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
            <div>
              <div className="text-sm">Emojis in Börsentexten</div>
              <div className="text-xs text-muted-foreground">Für mobile.de/AutoScout24 empfohlen: aus</div>
            </div>
            <Switch checked={!!ton.emojis_erlaubt} onCheckedChange={(v) => setTon((t) => ({ ...t, emojis_erlaubt: v }))} data-testid="settings-emojis-switch" />
          </div>
          <div className="space-y-1.5">
            <Label>Call-to-Action</Label>
            <Input value={ton.cta || ""} onChange={(e) => setTon((t) => ({ ...t, cta: e.target.value }))} data-testid="settings-cta-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Verbotene Begriffe (Komma-getrennt)</Label>
            <Input value={(ton.verbotene_begriffe || []).join(", ")}
              onChange={(e) => setTon((t) => ({ ...t, verbotene_begriffe: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
              data-testid="settings-forbidden-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Standard-Disclaimer</Label>
            <Textarea rows={2} value={ton.standard_disclaimer || ""} onChange={(e) => setTon((t) => ({ ...t, standard_disclaimer: e.target.value }))} data-testid="settings-disclaimer-input" />
          </div>
          <Button onClick={saveTonality} disabled={busy} className="gap-2" data-testid="settings-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
          </Button>
        </CardContent>
      </Card>

      {/* Hilfe & Tour */}
      <Card data-tour="settings-help" data-testid="settings-help-card">
        <CardContent className="pt-4 pb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <HelpCircle className="h-4 w-4 shrink-0 text-primary mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-medium">Interaktive Einführung</div>
              <div className="text-xs text-muted-foreground">Der Rundgang zeigt Schritt für Schritt den Weg vom Fahrzeug bis zum fertigen Inserat.</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={startTour} data-testid="settings-start-tour-button">
            <PlayCircle className="h-3.5 w-3.5" /> Tour starten
          </Button>
        </CardContent>
      </Card>

      {/* Benutzer (Phase 1) */}
      <Card>
        <CardContent className="pt-4 pb-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="settings-users-hint">
          <Users className="h-4 w-4 shrink-0" />
          Weitere Benutzer und Rollen werden später unterstützt. In der aktuellen Version reicht ein Zugang pro Autohaus.
        </CardContent>
      </Card>
    </div>
  );
}
